/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

// Client-side Web Push helpers: feature detection, (un)subscribe, and the
// server handshake. The push event handlers live in web/public/push-sw.js.

import { getApiUrl } from "@/utils/baseUrl";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushState = "unsupported" | "ios-browser" | "insecure" | "denied" | "off" | "on";

export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isInstalledPWA(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await getRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Resolve the current push state for the UI. */
export async function getPushState(): Promise<PushState> {
  if (!isPushSupported()) {
    // On iOS, PushManager only exists inside an installed PWA (home screen).
    if (isIOS() && !isInstalledPWA()) return "ios-browser";
    return "unsupported";
  }
  // Push requires a secure context (https) or localhost.
  if (!window.isSecureContext) return "insecure";
  if (Notification.permission === "denied") return "denied";
  const sub = await currentSubscription();
  return sub ? "on" : "off";
}

async function getVapidPublicKey(): Promise<string> {
  const res = await fetch(getApiUrl("/notifications/webpush/vapid"), {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Push notifications are not available on the server");
  const data = (await res.json()) as { publicKey?: string };
  if (!data.publicKey) throw new Error("Server did not return a VAPID key");
  return data.publicKey;
}

/** A failure with a plain-language cause and, where there is one, a fix. */
export class PushError extends Error {
  hint?: string;
  constructor(message: string, hint?: string) {
    super(message);
    this.name = "PushError";
    this.hint = hint;
  }
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string, hint?: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new PushError(message, hint)), ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

/** Request permission, subscribe, and register the subscription server-side. */
export async function enablePush(): Promise<void> {
  if (!isPushSupported()) {
    throw new PushError("This browser does not support push notifications.");
  }
  if (!window.isSecureContext) {
    throw new PushError(
      "Push needs a secure connection.",
      "Open the panel over HTTPS (or http://localhost); browsers refuse push on plain HTTP.",
    );
  }

  const permission = await Notification.requestPermission();
  if (permission === "denied") {
    throw new PushError(
      "Notifications are blocked for this site.",
      "Click the lock icon next to the address, allow Notifications, then reload the page.",
    );
  }
  if (permission !== "granted") {
    throw new PushError(
      "The permission prompt was dismissed.",
      "Press Enable again and choose Allow in the browser prompt.",
    );
  }

  const reg = await withTimeout(
    getRegistration(),
    8000,
    "The service worker isn't running yet.",
    "Reload the page once (Ctrl+Shift+R) and try again; a service worker is needed for push.",
  );

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    let key: string;
    try {
      key = await getVapidPublicKey();
    } catch (e) {
      throw new PushError(e instanceof Error ? e.message : "The server has no push key.");
    }
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    } catch (e) {
      const name = e instanceof Error ? e.name : "";
      if (name === "NotAllowedError") {
        throw new PushError("The browser refused to subscribe.", "Allow Notifications for this site in the browser's site settings.");
      }
      throw new PushError(
        "The browser couldn't reach its push service.",
        "Chrome and Edge need access to Google's push service (FCM); Brave: turn on “Use Google Services for Push Messaging” in brave://settings/privacy; Chromium builds without Google services, some VPNs/blockers and Yandex Browser can't do push at all. Firefox and Safari use their own services.",
      );
    }
  }

  const res = await fetch(getApiUrl("/notifications/webpush/subscribe"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(sub.toJSON()),
  });
  if (!res.ok) {
    throw new PushError("The panel rejected the subscription.", `The server answered ${res.status}. Sign in again and retry.`);
  }
}

/** Unsubscribe locally and drop the subscription server-side. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  await sub.unsubscribe().catch(() => undefined);
  await fetch(getApiUrl("/notifications/webpush/unsubscribe"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ endpoint }),
  }).catch(() => undefined);
}

/** Ask the server to push a test notification to all subscriptions. */
export async function sendTestPush(): Promise<void> {
  const res = await fetch(getApiUrl("/notifications/webpush/test"), {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to send a test notification");
}
